// Utilitários da Análise de Atendimento (tela). A planilha exportada não usa este arquivo.

const formatoInteiro = new Intl.NumberFormat("pt-BR");
const formatoDecimal = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const formatoPercentual = new Intl.NumberFormat("pt-BR", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatoPercentualCurto = new Intl.NumberFormat("pt-BR", { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 1 });

export const formatarNumero = (valor) => formatoInteiro.format(valor || 0);
export const formatarDecimal = (valor) => (valor === null || valor === undefined || Number.isNaN(valor) ? "-" : formatoDecimal.format(valor));
export const formatarPercentual = (valor) => (valor === null || valor === undefined ? "" : formatoPercentual.format(valor));
export const formatarPercentualCurto = (valor) => (valor === null || valor === undefined ? "" : formatoPercentualCurto.format(valor));

// "1. Janeiro.2026" -> "Jan/2026"
export const rotuloCurtoMes = (rotulo) => {
    const semNumero = String(rotulo).split(". ")[1] || String(rotulo);
    const [nome, ano] = semNumero.split(".");
    return ano ? `${nome.slice(0, 3)}/${ano}` : nome;
};

// Segmentação fixa de clientes do relatório (mesma do backend)
export const CLIENTES = ["CIELO", "COBEXTRA", "GETNET", "PLUXEE", "UMC"];

// Paleta categórica validada (skill dataviz) contra as superfícies do sistema (#fafafa / #4B4F5C).
// Cor identifica pessoa (negociador/coordenador); clientes são identificados pelo nome.
const CORES_PESSOAS = {
    light: { slots: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100"], outros: "#898781" },
    dark: { slots: ["#3987e5", "#d95926", "#199e70", "#c98500"], outros: "#b5b4ab" },
};
export const ROTULO_OUTROS = "Outros";

/**
 * Cores por pessoa no período: as 4 com mais atendimentos têm cor própria e as demais ficam em "Outros".
 * O ranking não depende dos filtros de cliente/pessoa, então filtrar não repinta ninguém.
 */
export function coresPorPessoa(blocos, darkMode) {
    const paleta = CORES_PESSOAS[darkMode ? "dark" : "light"];
    const destaques = [...blocos]
        .sort((a, b) => (b.atendimentosTotal || 0) - (a.atendimentosTotal || 0))
        .slice(0, paleta.slots.length)
        .map(b => b.nome);
    return {
        destaques,
        corOutros: paleta.outros,
        cor: (nome) => {
            const idx = destaques.indexOf(nome);
            return idx >= 0 ? paleta.slots[idx] : paleta.outros;
        },
    };
}

/**
 * Participação de cada pessoa nas fichas acionadas de cada cliente (cada cliente soma 100%).
 * Base: fichas acionadas mês a mês, a mesma do % de tempo.
 */
export function participacaoPorCliente(blocos, cliente) {
    const partes = blocos
        .map(b => {
            const linha = b.linhas.find(l => l.cliente === cliente);
            return { nome: b.nome, quantidade: linha ? linha.quantidadeTotal : 0 };
        })
        .filter(p => p.quantidade > 0);
    const total = partes.reduce((acc, p) => acc + p.quantidade, 0);
    return { total, partes: partes.map(p => ({ ...p, percentual: total > 0 ? p.quantidade / total : 0 })) };
}

// Medidas usam cores fora da paleta dos clientes, para não se confundirem com eles
const CORES_MEDIDAS = {
    fichas: { light: "#4a3aa7", dark: "#9085e9" },
    atendimentos: { light: "#008300", dark: "#008300" },
};

export const corMedida = (medida, darkMode) => CORES_MEDIDAS[medida][darkMode ? "dark" : "light"];

export const temaGrafico = (darkMode) => ({
    texto: darkMode ? "rgba(255, 255, 255, 0.9)" : "#344767",
    textoSecundario: darkMode ? "rgba(255, 255, 255, 0.65)" : "#7b809a",
    grade: darkMode ? "rgba(255, 255, 255, 0.10)" : "#e9ecef",
    superficie: darkMode ? "#4B4F5C" : "#fafafa",
    borda: darkMode ? "rgba(255, 255, 255, 0.12)" : "#e0e0e0",
});

// Texto legível sobre um fundo colorido (rótulos dentro das barras)
export const corTextoSobre = (hex) => {
    const valor = hex.replace("#", "");
    const [r, g, b] = [0, 2, 4].map(i => parseInt(valor.substring(i, i + 2), 16) / 255)
        .map(c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    const luminancia = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminancia > 0.3 ? "#0b0b0b" : "#ffffff";
};

const somar = (lista) => lista.reduce((acc, v) => acc + (v || 0), 0);
const somarPorMes = (listas, qtdMeses) => Array.from({ length: qtdMeses }, (_, i) => somar(listas.map(l => (l ? l[i] : 0))));

/**
 * Soma os blocos de várias pessoas num único bloco (mesmo formato do backend),
 * recalculando o % de tempo por cliente. Usado para a visão "Todos".
 */
export function consolidarBlocos(blocos, nome, qtdMeses) {
    const porCliente = {};
    blocos.forEach(bloco => bloco.linhas.forEach(linha => {
        const acc = porCliente[linha.cliente] || (porCliente[linha.cliente] = { quantidades: [], atendimentos: [], atendimentosTotal: 0, fichasPeriodo: 0 });
        acc.quantidades.push(linha.quantidades);
        acc.atendimentos.push(linha.atendimentos || []);
        acc.atendimentosTotal += linha.atendimentosTotal || 0;
        acc.fichasPeriodo += linha.fichasPeriodo || 0;
    }));

    const clientes = Object.keys(porCliente).sort();
    const quantidadesPorCliente = clientes.map(c => somarPorMes(porCliente[c].quantidades, qtdMeses));
    const totaisMes = somarPorMes(quantidadesPorCliente, qtdMeses);
    const totalGeral = somar(totaisMes);

    const linhas = clientes.map((cliente, idx) => {
        const quantidades = quantidadesPorCliente[idx];
        const quantidadeTotal = somar(quantidades);
        return {
            cliente,
            quantidades,
            percentuais: quantidades.map((q, i) => (q > 0 ? q / totaisMes[i] : null)),
            quantidadeTotal,
            percentualTotal: totalGeral > 0 ? quantidadeTotal / totalGeral : null,
            atendimentos: somarPorMes(porCliente[cliente].atendimentos, qtdMeses),
            atendimentosTotal: porCliente[cliente].atendimentosTotal,
            fichasPeriodo: porCliente[cliente].fichasPeriodo,
        };
    });

    return {
        nome,
        linhas,
        totaisMes,
        totalGeral,
        atendimentosMes: somarPorMes(linhas.map(l => l.atendimentos), qtdMeses),
        atendimentosTotal: somar(linhas.map(l => l.atendimentosTotal)),
        fichasPeriodo: somar(linhas.map(l => l.fichasPeriodo)),
    };
}

/**
 * Esforço de uma pessoa em cada cliente no período, na ordem fixa dos clientes.
 * percentual = fatia das fichas acionadas mês a mês (mesma base da coluna "Total Geral" da planilha);
 * fichas = fichas distintas acionadas no período; atendimentos = registros de histórico.
 */
export const esforcoPorCliente = (bloco) => CLIENTES.map(cliente => {
    const linha = bloco.linhas.find(l => l.cliente === cliente);
    return {
        cliente,
        percentual: linha ? linha.percentualTotal || 0 : 0,
        fichas: linha ? linha.fichasPeriodo || 0 : 0,
        atendimentos: linha ? linha.atendimentosTotal || 0 : 0,
    };
});

export const clientePrincipal = (bloco) => bloco.linhas.reduce(
    (maior, linha) => (!maior || (linha.percentualTotal || 0) > (maior.percentualTotal || 0) ? linha : maior),
    null
);

/**
 * Números de uma pessoa no escopo do filtro de cliente.
 * fichasMes soma os clientes: cada ficha é de um único cliente, então não há duplicidade.
 */
export function numerosDaPessoa(bloco, cliente, qtdMeses) {
    const linhas = cliente ? bloco.linhas.filter(l => l.cliente === cliente) : bloco.linhas;
    return {
        fichas: somar(linhas.map(l => l.fichasPeriodo)),
        atendimentos: somar(linhas.map(l => l.atendimentosTotal)),
        fichasMes: somarPorMes(linhas.map(l => l.quantidades), qtdMeses),
        atendimentosMes: somarPorMes(linhas.map(l => l.atendimentos), qtdMeses),
    };
}

/**
 * Indicadores do topo da tela.
 * Sem pessoa selecionada usa os totais gerais por cliente do backend, que contam cada ficha uma vez
 * mesmo quando acionada por mais de um negociador.
 */
export function calcularIndicadores(dados, blocos, pessoaNome, cliente) {
    const qtdMeses = dados?.meses?.length || 0;
    if (!dados || qtdMeses === 0) {
        return null;
    }

    let numeros;
    let blocoDedicacao;
    if (pessoaNome) {
        const bloco = blocos.find(b => b.nome === pessoaNome);
        if (!bloco) return null;
        numeros = numerosDaPessoa(bloco, cliente, qtdMeses);
        blocoDedicacao = bloco;
    } else {
        const totais = (dados.clientes || []).filter(c => !cliente || c.cliente === cliente);
        numeros = {
            fichas: somar(totais.map(c => c.fichasPeriodo)),
            atendimentos: somar(totais.map(c => c.atendimentosPeriodo)),
            fichasMes: somarPorMes(totais.map(c => c.fichasMes), qtdMeses),
            atendimentosMes: somarPorMes(totais.map(c => c.atendimentosMes), qtdMeses),
        };
        blocoDedicacao = consolidarBlocos(blocos, "Todos", qtdMeses);
    }

    const percentuaisPorCliente = {};
    blocoDedicacao.linhas.forEach(l => { percentuaisPorCliente[l.cliente] = l.percentualTotal || 0; });

    return {
        ...numeros,
        mediaPorFicha: numeros.fichas > 0 ? numeros.atendimentos / numeros.fichas : null,
        principal: clientePrincipal(blocoDedicacao),
        percentuaisPorCliente,
    };
}

/** Trimestre imediatamente anterior (1º trimestre volta para o 4º do ano anterior). */
export const trimestreAnterior = (ano, trimestre) => (trimestre > 1 ? { ano, trimestre: trimestre - 1 } : { ano: ano - 1, trimestre: 4 });

/** Variação percentual entre dois valores; null quando não há base de comparação. */
export const variacaoPercentual = (atual, anterior) => (
    atual === null || atual === undefined || !anterior ? null : (atual - anterior) / anterior
);
